import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Input } from '@medikei/ui';
import { Card, CardContent } from '@medikei/ui';
import { Badge } from '@medikei/ui';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/Textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  X,
  Stethoscope,
  AlertTriangle,
  Clock,
  DollarSign,
  Tag,
  Video,
  Building,
  Home,
} from 'lucide-react';
import { httpClient } from '@/api';
import { toast } from '@/components/ui/use-toast';

const POPULAR_SPECIALTY_ICONS = [
  'Stethoscope',
  'Heart',
  'Brain',
  'Baby',
  'Bone',
  'Eye',
  'Pill',
  'Activity',
  'Sparkles',
  'Shield',
  'Ear',
  'Syringe',
];

interface ModalityFinancialForm {
  price: number;
  directCost: number;
  commissionPercent: number;
  taxPercent: number;
}

interface Specialty {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { doctors: number };
  durationMinutes?: number;
  pricing?: {
    telemedicine?: ModalityFinancialForm & { commissionValue?: number; taxValue?: number; netDoctorAmount?: number; marginPercent?: number };
    inPerson?: ModalityFinancialForm & { commissionValue?: number; taxValue?: number; netDoctorAmount?: number; marginPercent?: number };
    homeCare?: ModalityFinancialForm & { commissionValue?: number; taxValue?: number; netDoctorAmount?: number; marginPercent?: number };
    durationMinutes?: number;
  };
  scheduledPrice?: number;
  inPersonPrice?: number;
  homeCarePrice?: number;
}

interface SpecialtiesResponse {
  data: Specialty[];
}

interface SpecialtyFormData {
  name: string;
  description: string;
  icon: string;
  durationMinutes: number;
  telemedicine: ModalityFinancialForm;
  inPerson: ModalityFinancialForm;
  homeCare: ModalityFinancialForm;
}

interface FormErrors {
  name?: string;
}

type ModalTab = 'identificacao' | 'agenda' | 'financeiro';
type FinancialModality = 'telemedicina' | 'presencial' | 'domiciliar';

function calculateFinancialSplit(price: number, directCost: number, commissionPercent: number, taxPercent: number) {
  const p = Math.max(0, Number(price) || 0);
  const cost = Math.max(0, Number(directCost) || 0);
  const commPct = Math.max(0, Number(commissionPercent) || 0);
  const taxPct = Math.max(0, Number(taxPercent) || 0);

  const commissionVal = Math.round(p * (commPct / 100) * 100) / 100;
  const taxVal = Math.round(p * (taxPct / 100) * 100) / 100;
  const netDoctor = Math.max(0, Math.round((p - cost - commissionVal - taxVal) * 100) / 100);
  const marginPct = p > 0 ? Math.round((netDoctor / p) * 10000) / 100 : 0;
  const platformRetained = Math.round((cost + commissionVal + taxVal) * 100) / 100;

  return {
    price: p,
    directCost: cost,
    commissionPercent: commPct,
    taxPercent: taxPct,
    commissionVal,
    taxVal,
    netDoctor,
    marginPct,
    platformRetained,
  };
}

export function AdminSpecialtyManagementPage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSpecialty, setEditingSpecialty] = useState<Specialty | null>(null);
  const [modalTab, setModalTab] = useState<ModalTab>('identificacao');
  const [financialModality, setFinancialModality] = useState<FinancialModality>('telemedicina');

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState<string>('');

  const [formData, setFormData] = useState<SpecialtyFormData>({
    name: '',
    description: '',
    icon: 'Stethoscope',
    durationMinutes: 30,
    telemedicine: {
      price: 120,
      directCost: 20,
      commissionPercent: 15,
      taxPercent: 6,
    },
    inPerson: {
      price: 180,
      directCost: 30,
      commissionPercent: 15,
      taxPercent: 6,
    },
    homeCare: {
      price: 250,
      directCost: 40,
      commissionPercent: 15,
      taxPercent: 6,
    },
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const { data, isLoading } = useQuery<SpecialtiesResponse>({
    queryKey: ['admin', 'specialties'],
    queryFn: async () => {
      const res = await httpClient.get<{ success?: boolean; data: Specialty[] } | Specialty[]>('/admin/specialties');
      if (Array.isArray(res)) {
        return { data: res };
      }
      return { data: res?.data || [] };
    },
  });

  const createSpecialtyMutation = useMutation({
    mutationFn: async (payload: any) => {
      return httpClient.post('/admin/specialties', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'specialties'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'config'] });
      closeModal();
    },
  });

  const updateSpecialtyMutation = useMutation({
    mutationFn: async ({ id, ...payload }: any) => {
      return httpClient.patch(`/admin/specialties/${id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'specialties'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'config'] });
      closeModal();
    },
  });

  const toggleSpecialtyMutation = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      return httpClient.patch(`/admin/specialties/${id}/toggle`, { active });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'specialties'] });
    },
  });

  const deleteSpecialtyMutation = useMutation({
    mutationFn: async (id: string) => {
      return httpClient.delete(`/admin/specialties/${id}`);
    },
    onSuccess: () => {
      toast({
        title: 'Especialidade excluída com sucesso',
        description: 'A especialidade inativa foi removida do sistema.',
        variant: 'default',
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'specialties'] });
      setDeleteConfirmId(null);
      setDeleteConfirmName('');
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao excluir especialidade',
        description: err?.message || 'Verifique se existem médicos ou dependências ativas associadas.',
        variant: 'error',
      });
    },
  });

  const rawData: any = data;
  const rawList: any[] = Array.isArray(rawData)
    ? rawData
    : Array.isArray(rawData?.data)
    ? rawData.data
    : [];

  const specialties: Specialty[] = useMemo(() => {
    return rawList.map((item: any) => {
      const specObj = item?.specialty && typeof item.specialty === 'object' ? item.specialty : item;
      return {
        ...specObj,
        _count: { doctors: item.doctorCount ?? item.specialty?._count?.doctors ?? item._count?.doctors ?? 0 },
        pricing: item.pricing,
        scheduledPrice: item.scheduledPrice ?? item.pricing?.telemedicine?.price ?? 120,
        inPersonPrice: item.inPersonPrice ?? item.pricing?.inPerson?.price ?? 180,
        homeCarePrice: item.homeCarePrice ?? item.pricing?.homeCare?.price ?? 250,
        durationMinutes: item.durationMinutes ?? item.pricing?.durationMinutes ?? 30,
      };
    });
  }, [rawList]);

  const filteredSpecialties = useMemo(() => {
    return specialties.filter((specialty) => {
      if (!specialty || typeof specialty.name !== 'string') return false;
      const term = (searchQuery || '').toLowerCase().trim();
      if (!term) return true;
      return (
        specialty.name.toLowerCase().includes(term) ||
        (typeof specialty.description === 'string' && specialty.description.toLowerCase().includes(term))
      );
    });
  }, [specialties, searchQuery]);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    const name = formData.name.trim();

    if (!name) {
      errors.name = 'Nome é obrigatório';
    } else if (name.length < 2) {
      errors.name = 'Nome deve ter pelo menos 2 caracteres';
    } else if (name.length > 100) {
      errors.name = 'Nome deve ter no máximo 100 caracteres';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openCreateModal = () => {
    setEditingSpecialty(null);
    setModalTab('identificacao');
    setFinancialModality('telemedicina');
    setFormData({
      name: '',
      description: '',
      icon: 'Stethoscope',
      durationMinutes: 30,
      telemedicine: { price: 120, directCost: 20, commissionPercent: 15, taxPercent: 6 },
      inPerson: { price: 180, directCost: 30, commissionPercent: 15, taxPercent: 6 },
      homeCare: { price: 250, directCost: 40, commissionPercent: 15, taxPercent: 6 },
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (specialty: Specialty) => {
    setEditingSpecialty(specialty);
    setModalTab('financeiro');
    setFinancialModality('telemedicina');

    const pr = specialty.pricing;
    setFormData({
      name: specialty.name,
      description: specialty.description || '',
      icon: specialty.icon || 'Stethoscope',
      durationMinutes: specialty.durationMinutes ?? pr?.durationMinutes ?? 30,
      telemedicine: {
        price: pr?.telemedicine?.price ?? specialty.scheduledPrice ?? 120,
        directCost: pr?.telemedicine?.directCost ?? 20,
        commissionPercent: pr?.telemedicine?.commissionPercent ?? 15,
        taxPercent: pr?.telemedicine?.taxPercent ?? 6,
      },
      inPerson: {
        price: pr?.inPerson?.price ?? specialty.inPersonPrice ?? 180,
        directCost: pr?.inPerson?.directCost ?? 30,
        commissionPercent: pr?.inPerson?.commissionPercent ?? 15,
        taxPercent: pr?.inPerson?.taxPercent ?? 6,
      },
      homeCare: {
        price: pr?.homeCare?.price ?? specialty.homeCarePrice ?? 250,
        directCost: pr?.homeCare?.directCost ?? 40,
        commissionPercent: pr?.homeCare?.commissionPercent ?? 15,
        taxPercent: pr?.homeCare?.taxPercent ?? 6,
      },
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSpecialty(null);
    setFormErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      setModalTab('identificacao');
      return;
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      icon: formData.icon.trim() || undefined,
      durationMinutes: Number(formData.durationMinutes) || 30,
      pricing: {
        telemedicine: formData.telemedicine,
        inPerson: formData.inPerson,
        homeCare: formData.homeCare,
        durationMinutes: Number(formData.durationMinutes) || 30,
      },
    };

    if (editingSpecialty) {
      updateSpecialtyMutation.mutate({ id: editingSpecialty.id, ...payload });
    } else {
      createSpecialtyMutation.mutate(payload);
    }
  };

  const handleToggle = (specialty: Specialty) => {
    toggleSpecialtyMutation.mutate({ id: specialty.id, active: !specialty.active });
  };

  const handleDeleteClick = (specialty: Specialty) => {
    if (specialty.active) {
      toast({
        title: 'Especialidade Ativa',
        description: 'Apenas especialidades inativas podem ser excluídas. Desative a especialidade na tabela antes de prosseguir.',
        variant: 'error',
      });
      return;
    }
    setDeleteConfirmId(specialty.id);
    setDeleteConfirmName(specialty.name);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmId) {
      deleteSpecialtyMutation.mutate(deleteConfirmId);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
    setDeleteConfirmName('');
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(val) || 0);
  };

  // Cálculo financeiro da modalidade atualmente ativa no formulário
  const currentFinancial = useMemo(() => {
    const activeData =
      financialModality === 'telemedicina'
        ? formData.telemedicine
        : financialModality === 'presencial'
        ? formData.inPerson
        : formData.homeCare;

    return calculateFinancialSplit(
      activeData.price,
      activeData.directCost,
      activeData.commissionPercent,
      activeData.taxPercent,
    );
  }, [formData, financialModality]);

  const updateCurrentFinancialField = (field: keyof ModalityFinancialForm, value: number) => {
    setFormData((prev) => {
      const targetKey =
        financialModality === 'telemedicina'
          ? 'telemedicine'
          : financialModality === 'presencial'
          ? 'inPerson'
          : 'homeCare';

      return {
        ...prev,
        [targetKey]: {
          ...prev[targetKey],
          [field]: value,
        },
      };
    });
  };

  return (
    <div className="space-y-6 text-foreground p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-2xl bg-primary/10 text-primary shrink-0 border border-primary/20 shadow-xs">
            <Stethoscope className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Gerenciamento de Especialidades
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
              Defina os parâmetros clínicos, tempo de agenda e matriz financeira das modalidades médicas
            </p>
          </div>
        </div>

        <Button
          onClick={openCreateModal}
          className="rounded-xl font-bold h-11 px-5 shadow-md shadow-primary/20 gap-2 shrink-0 self-start sm:self-auto cursor-pointer"
        >
          <Plus className="h-4 w-4" />
          <span>Nova Especialidade</span>
        </Button>
      </div>

      {/* Barra de Pesquisa */}
      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Buscar por nome ou descrição..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-11 rounded-xl bg-card border-border text-foreground focus:ring-2 focus:ring-primary/20"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground p-1 rounded-lg transition-colors cursor-pointer"
            aria-label="Limpar busca"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Tabela de Especialidades com Preços Integrados */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <Card className="rounded-3xl border border-border shadow-xs overflow-hidden bg-card">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/40 border-b border-border">
                  <tr>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Especialidade
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Telemedicina
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Presencial
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Domiciliar
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-5 py-3.5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60 bg-card">
                  {filteredSpecialties.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-16 text-center">
                        <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-3 shadow-xs">
                          <Stethoscope className="h-7 w-7" />
                        </div>
                        <h4 className="text-base font-bold text-foreground">
                          {searchQuery ? 'Nenhuma especialidade encontrada' : 'Nenhuma especialidade cadastrada'}
                        </h4>
                        <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                          {searchQuery
                            ? 'Tente buscar por outro termo ou limpe o campo de busca'
                            : 'Clique em "Nova Especialidade" para cadastrar a primeira'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredSpecialties.map((specialty) => (
                      <tr key={specialty.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-5 py-4 whitespace-nowrap">
                          <div className="font-bold text-foreground text-sm">{specialty.name}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {specialty._count?.doctors || 0} médico{(specialty._count?.doctors || 0) !== 1 ? 's' : ''} associado{(specialty._count?.doctors || 0) !== 1 ? 's' : ''}
                            {specialty.durationMinutes ? ` • ${specialty.durationMinutes} min` : ''}
                          </div>
                        </td>

                        {/* Preço Telemedicina */}
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className="font-bold text-sm text-foreground">
                            {formatCurrency(specialty.scheduledPrice ?? 120)}
                          </span>
                          <span className="block text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                            Líquido: {formatCurrency(specialty.pricing?.telemedicine?.netDoctorAmount ?? ((specialty.scheduledPrice ?? 120) * 0.7))}
                          </span>
                        </td>

                        {/* Preço Presencial */}
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className="font-bold text-sm text-foreground">
                            {formatCurrency(specialty.inPersonPrice ?? 180)}
                          </span>
                          <span className="block text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                            Líquido: {formatCurrency(specialty.pricing?.inPerson?.netDoctorAmount ?? ((specialty.inPersonPrice ?? 180) * 0.7))}
                          </span>
                        </td>

                        {/* Preço Domiciliar */}
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className="font-bold text-sm text-foreground">
                            {formatCurrency(specialty.homeCarePrice ?? 250)}
                          </span>
                          <span className="block text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                            Líquido: {formatCurrency(specialty.pricing?.homeCare?.netDoctorAmount ?? ((specialty.homeCarePrice ?? 250) * 0.7))}
                          </span>
                        </td>

                        {/* Status Ativo */}
                        <td className="px-5 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={specialty.active}
                              onCheckedChange={() => handleToggle(specialty)}
                              disabled={toggleSpecialtyMutation.isPending}
                            />
                            <Badge
                              variant={specialty.active ? 'success' : 'outline'}
                              className="text-[11px] font-bold"
                            >
                              {specialty.active ? 'Ativo' : 'Inativo'}
                            </Badge>
                          </div>
                        </td>

                        {/* Ações */}
                        <td className="px-5 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => openEditModal(specialty)}
                              className="p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-xl transition-all cursor-pointer"
                              title="Editar especialidade e matriz financeira"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              disabled={specialty.active}
                              onClick={() => handleDeleteClick(specialty)}
                              className={
                                specialty.active
                                  ? 'p-2 text-muted-foreground/30 cursor-not-allowed rounded-xl transition-all'
                                  : 'p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-xl transition-all cursor-pointer'
                              }
                              title={
                                specialty.active
                                  ? 'Apenas especialidades inativas podem ser excluídas. Desative a chave primeiro.'
                                  : 'Excluir especialidade inativa'
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal Completo com Abas e Financeiro (Idêntico ao modelo anexado) */}
      <Dialog open={isModalOpen} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden rounded-3xl border border-border shadow-2xl bg-card">
          <DialogHeader className="px-6 py-5 border-b border-border bg-card">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-xl font-extrabold text-foreground">
                  {editingSpecialty ? 'Editar Procedimento / Especialidade' : 'Novo Procedimento / Especialidade'}
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                  Atualize os dados clínicos e a estrutura financeira deste serviço
                </DialogDescription>
              </div>
            </div>

            {/* Abas Superiores do Modal (Estilo Imagem Anexada) */}
            <div className="flex items-center gap-2 pt-4 overflow-x-auto pb-1 scrollbar-none text-xs">
              <button
                type="button"
                onClick={() => setModalTab('identificacao')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-bold transition-all cursor-pointer whitespace-nowrap ${
                  modalTab === 'identificacao'
                    ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900 shadow-xs'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                }`}
              >
                <Tag className="w-3.5 h-3.5" />
                <span>Identificação</span>
              </button>

              <button
                type="button"
                onClick={() => setModalTab('agenda')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-bold transition-all cursor-pointer whitespace-nowrap ${
                  modalTab === 'agenda'
                    ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900 shadow-xs'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                }`}
              >
                <Clock className="w-3.5 h-3.5" />
                <span>Tempo e Agenda</span>
              </button>

              <button
                type="button"
                onClick={() => setModalTab('financeiro')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-bold transition-all cursor-pointer whitespace-nowrap ${
                  modalTab === 'financeiro'
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20'
                }`}
              >
                <DollarSign className="w-3.5 h-3.5" />
                <span>Financeiro</span>
              </button>
            </div>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="p-6 text-foreground space-y-5 max-h-[60vh] overflow-y-auto">
              {/* ABA 1: Identificação */}
              {modalTab === 'identificacao' && (
                <div className="space-y-4 animate-in fade-in duration-150">
                  <div>
                    <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Nome da Especialidade / Procedimento <span className="text-destructive">*</span>
                    </label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ex: Cardiologia"
                      maxLength={100}
                      className="rounded-xl h-11 bg-background border-input text-foreground focus:ring-2 focus:ring-primary/20"
                    />
                    {formErrors.name && (
                      <p className="mt-1 text-xs font-medium text-destructive">{formErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Descrição &amp; Escopo Clínico
                    </label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Breve descrição dos cuidados e escopo deste atendimento..."
                      rows={3}
                      className="rounded-xl bg-background border-input text-foreground resize-none focus:ring-2 focus:ring-primary/20 text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="icon" className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Ícone Representativo (Lucide)
                    </label>
                    <Input
                      id="icon"
                      type="text"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      placeholder="Ex: Stethoscope, Heart, Brain"
                      className="rounded-xl h-11 bg-background border-input text-foreground focus:ring-2 focus:ring-primary/20"
                    />

                    <div className="mt-2.5">
                      <span className="text-[11px] font-medium text-muted-foreground block mb-1.5">
                        Sugestões de Ícones:
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {POPULAR_SPECIALTY_ICONS.map((iconName) => (
                          <button
                            key={iconName}
                            type="button"
                            onClick={() => setFormData({ ...formData, icon: iconName })}
                            className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg border transition-all cursor-pointer ${
                              formData.icon === iconName
                                ? 'bg-primary text-primary-foreground border-primary shadow-xs'
                                : 'bg-muted/50 hover:bg-muted text-muted-foreground border-border/80'
                            }`}
                          >
                            {iconName}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ABA 2: Tempo e Agenda */}
              {modalTab === 'agenda' && (
                <div className="space-y-4 animate-in fade-in duration-150">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Duração Padrão do Atendimento (Minutos)
                    </label>
                    <div className="flex items-center gap-2">
                      {[20, 30, 45, 60].map((mins) => (
                        <button
                          key={mins}
                          type="button"
                          onClick={() => setFormData({ ...formData, durationMinutes: mins })}
                          className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                            formData.durationMinutes === mins
                              ? 'bg-primary text-primary-foreground border-primary shadow-xs'
                              : 'bg-muted/30 border-border text-foreground hover:bg-muted'
                          }`}
                        >
                          {mins} min
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="p-4 rounded-2xl bg-muted/30 border border-border space-y-1">
                    <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-primary" />
                      Regra de Alocação de Slots
                    </span>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Ao criar a agenda médica para esta especialidade, cada vaga utilizará a duração padrão de{' '}
                      <strong>{formData.durationMinutes} minutos</strong> para agendamentos de telemedicina e presenciais.
                    </p>
                  </div>
                </div>
              )}

              {/* ABA 3: Financeiro (EXATAMENTE COMO A IMAGEM ANEXADA) */}
              {modalTab === 'financeiro' && (
                <div className="space-y-5 animate-in fade-in duration-150">
                  {/* Seletor de Modalidade (Pills) */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                      Selecione a Modalidade para Configurar:
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => setFinancialModality('telemedicina')}
                        className={`flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-2xl font-bold text-xs transition-all cursor-pointer border ${
                          financialModality === 'telemedicina'
                            ? 'bg-primary text-primary-foreground border-primary shadow-xs'
                            : 'bg-muted/30 border-border text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Video className="w-3.5 h-3.5" />
                        <span>Telemedicina</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setFinancialModality('presencial')}
                        className={`flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-2xl font-bold text-xs transition-all cursor-pointer border ${
                          financialModality === 'presencial'
                            ? 'bg-primary text-primary-foreground border-primary shadow-xs'
                            : 'bg-muted/30 border-border text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Building className="w-3.5 h-3.5" />
                        <span>Presencial</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setFinancialModality('domiciliar')}
                        className={`flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-2xl font-bold text-xs transition-all cursor-pointer border ${
                          financialModality === 'domiciliar'
                            ? 'bg-primary text-primary-foreground border-primary shadow-xs'
                            : 'bg-muted/30 border-border text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Home className="w-3.5 h-3.5" />
                        <span>Domiciliar</span>
                      </button>
                    </div>
                  </div>

                  {/* Os 4 Campos da Imagem Anexada */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Campo 1: Preço de venda */}
                    <div>
                      <label className="block text-xs font-bold text-foreground mb-1">
                        Preço de venda (R$)
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={
                          financialModality === 'telemedicina'
                            ? formData.telemedicine.price
                            : financialModality === 'presencial'
                            ? formData.inPerson.price
                            : formData.homeCare.price
                        }
                        onChange={(e) => updateCurrentFinancialField('price', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                        className="rounded-xl h-11 text-sm font-semibold"
                      />
                      <span className="text-[10px] text-muted-foreground mt-0.5 block">
                        Valor cobrado do paciente pela consulta
                      </span>
                    </div>

                    {/* Campo 2: Custo direto */}
                    <div>
                      <label className="block text-xs font-bold text-foreground mb-1">
                        Custo direto (R$)
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={
                          financialModality === 'telemedicina'
                            ? formData.telemedicine.directCost
                            : financialModality === 'presencial'
                            ? formData.inPerson.directCost
                            : formData.homeCare.directCost
                        }
                        onChange={(e) => updateCurrentFinancialField('directCost', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                        className="rounded-xl h-11 text-sm font-semibold"
                      />
                      <span className="text-[10px] text-muted-foreground mt-0.5 block">
                        Custo operacional retido pela plataforma
                      </span>
                    </div>

                    {/* Campo 3: Comissão (%) */}
                    <div>
                      <label className="block text-xs font-bold text-foreground mb-1">
                        Comissão da Plataforma (%)
                      </label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={
                          financialModality === 'telemedicina'
                            ? formData.telemedicine.commissionPercent
                            : financialModality === 'presencial'
                            ? formData.inPerson.commissionPercent
                            : formData.homeCare.commissionPercent
                        }
                        onChange={(e) => updateCurrentFinancialField('commissionPercent', parseFloat(e.target.value) || 0)}
                        placeholder="15"
                        className="rounded-xl h-11 text-sm font-semibold"
                      />
                      <span className="text-[10px] text-muted-foreground mt-0.5 block">
                        Taxa de intermediação retida: {formatCurrency(currentFinancial.commissionVal)}
                      </span>
                    </div>

                    {/* Campo 4: Impostos (%) */}
                    <div>
                      <label className="block text-xs font-bold text-foreground mb-1">
                        Impostos (%)
                      </label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={
                          financialModality === 'telemedicina'
                            ? formData.telemedicine.taxPercent
                            : financialModality === 'presencial'
                            ? formData.inPerson.taxPercent
                            : formData.homeCare.taxPercent
                        }
                        onChange={(e) => updateCurrentFinancialField('taxPercent', parseFloat(e.target.value) || 0)}
                        placeholder="6"
                        className="rounded-xl h-11 text-sm font-semibold"
                      />
                      <span className="text-[10px] text-muted-foreground mt-0.5 block">
                        Impostos retidos: {formatCurrency(currentFinancial.taxVal)}
                      </span>
                    </div>
                  </div>

                  {/* Cards de Resultado Automático (Idênticos ao modelo anexado) */}
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    {/* Card Lucro Líquido / Repasse ao Médico */}
                    <div className="p-4 rounded-2xl bg-muted/40 dark:bg-neutral-900 border border-border">
                      <span className="text-xs text-muted-foreground font-semibold block mb-1">
                        Lucro líquido / Repasse
                      </span>
                      <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(currentFinancial.netDoctor)}
                      </p>
                      <span className="text-[10px] text-muted-foreground mt-0.5 block">
                        Creditado na carteira do médico
                      </span>
                    </div>

                    {/* Card Margem */}
                    <div className="p-4 rounded-2xl bg-muted/40 dark:bg-neutral-900 border border-border">
                      <span className="text-xs text-muted-foreground font-semibold block mb-1">
                        Margem do Médico
                      </span>
                      <p className="text-2xl font-black text-foreground">
                        {currentFinancial.marginPct.toFixed(0)}%
                      </p>
                      <span className="text-[10px] text-muted-foreground mt-0.5 block">
                        Percentual retido pela plataforma: {((currentFinancial.platformRetained / (currentFinancial.price || 1)) * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="px-6 py-4 border-t border-border bg-card/95 flex items-center justify-end gap-2.5">
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                disabled={createSpecialtyMutation.isPending || updateSpecialtyMutation.isPending}
                className="rounded-xl font-semibold h-10 px-5 cursor-pointer"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                isLoading={createSpecialtyMutation.isPending || updateSpecialtyMutation.isPending}
                className="rounded-xl font-bold h-10 px-6 shadow-md shadow-primary/20 bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer"
              >
                {editingSpecialty ? 'Salvar Alterações' : 'Criar Especialidade'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Exclusão */}
      {deleteConfirmId && (
        <Dialog open={true} onOpenChange={(open) => !open && handleDeleteCancel()}>
          <DialogContent className="max-w-md p-0 overflow-hidden rounded-3xl border border-border shadow-2xl bg-card">
            <DialogHeader className="px-6 py-5 border-b border-border bg-card">
              <DialogTitle className="text-lg font-bold text-destructive flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                <span>Confirmar Exclusão</span>
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                Esta ação removerá a especialidade do cadastro da plataforma
              </DialogDescription>
            </DialogHeader>

            <div className="p-6">
              <p className="text-sm text-foreground">
                Tem certeza que deseja excluir a especialidade <strong>{deleteConfirmName}</strong>?
              </p>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                Médicos associados a esta especialidade continuarão cadastrados, mas esta opção não estará mais disponível para novos agendamentos ou filtros públicos.
              </p>
            </div>

            <DialogFooter className="px-6 py-4 border-t border-border bg-card flex justify-end gap-2.5">
              <Button
                variant="outline"
                onClick={handleDeleteCancel}
                disabled={deleteSpecialtyMutation.isPending}
                className="rounded-xl font-semibold h-10 px-4 cursor-pointer"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                isLoading={deleteSpecialtyMutation.isPending}
                className="bg-destructive hover:bg-destructive/90 text-white rounded-xl font-bold h-10 px-5 shadow-sm shadow-destructive/20 cursor-pointer"
              >
                Excluir Especialidade
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
